/* Offline-correct clock-in / clock-out for the driver app.
 *
 * A punch is payroll evidence, so it is held to a harder standard than a
 * photo: a lost photo means a driver retakes it, a lost punch means an unpaid
 * day and an argument at the end of the fortnight. Two consequences run
 * through this file:
 *
 *   1. The DEVICE clock is what gets recorded (occurred_at, full ISO-8601
 *      with offset). A punch made in a dead zone at 06:58 and sent when the
 *      phone finds signal at 17:30 must still read 06:58 on the pay record.
 *   2. Nothing is ever dropped quietly. uploadQueue.flush() deletes a photo
 *      the server refuses; here a refused punch is kept, marked failed and
 *      put in front of the driver so the office can key it in by hand.
 *
 * This is a PAY RECORD, not a logbook. The legal logbook stays whatever the
 * driver already runs under Land Transport Rule: Work Time and Logbooks 2007;
 * hours flow logbook -> here, never the other way.
 *
 * Sending only happens while the app is open (mount, and the network-change
 * listener in DriverApp) - there is no Background Sync and sw.js has no sync
 * listener - so the UI says "will send when you're back on signal" rather
 * than pretending a queued punch is already with the office.
 *
 * The truck pre-check (NZTA daily checklist) rides in the same queue as a
 * fourth kind, "check". It is a vehicle safety record kept for years, so it
 * gets the punch's guarantees, not the photo's: never dropped, refused ones
 * shown to the driver. It is queued BEFORE the clock-in it belongs to and the
 * queue is strictly ordered, so the office always receives the check first.
 * A refused check does not undo the shift - the operator's rule is that a
 * fault warns and is recorded, it never strands a run.
 *
 * Breaks are RECORDED, not added: two more kinds, "break_start" and
 * "break_end", each a punch with the device time like a clock-in. Breaks stay
 * paid - the server never subtracts one from the hours - the point of the two
 * punches is that a break has a start and a finish the office can check. The
 * legacy "break" kind (a bare number of minutes) is still sendable so a punch
 * queued by an older copy of the app lands; the UI no longer offers it.
 * Because the queue is strictly ordered, a break end can never overtake the
 * start it belongs to.
 *
 * The bar is always drawn as "what the office has confirmed, plus what is
 * still held on this phone": every server answer (/time/current, a punch's
 * own reply, a flush) has the queued punches laid over it (overlayQueued)
 * before it is cached, so a punch waiting behind one just sent never drops
 * off the bar to be punched a second time.
 */

/* Shared with uploadQueue.js: same database, one store each. Both files
 * declare DB_VERSION and create BOTH stores, because whichever module opens
 * the database first runs the upgrade - and a module still asking for the
 * old version would be refused with a VersionError. */
const DB_NAME = 'dav-driver';
const DB_VERSION = 2;
const CLOCK_STORE = 'pending-clock';
const PHOTO_STORE = 'pending-photos';

const SHIFT_CACHE_KEY = 'dav_shift_state';
const NZ_TZ = 'Pacific/Auckland';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        db.createObjectStore(PHOTO_STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CLOCK_STORE)) {
        db.createObjectStore(CLOCK_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode) {
  return db.transaction(CLOCK_STORE, mode).objectStore(CLOCK_STORE);
}

/* ------------------------------------------------------------------ *
 * time helpers
 * ------------------------------------------------------------------ */

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

/* The server stores UTC but Python's isoformat() on a naive datetime emits no
 * "Z". Per the ES spec an offsetless date-time is parsed as LOCAL, so reading
 * one with new Date() shifts it 12-13 hours in NZ: a 19:43 delivery displays
 * as 07:43. Treat an offsetless server string as the UTC it actually is. */
export function parseServerInstant(value) {
  if (!value) return null;
  const raw = String(value).trim().replace(' ', 'T');
  const d = new Date(HAS_OFFSET.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "HH:MM" in New Zealand time. Use only where the API gives no pre-formatted
 *  NZ string (a locally queued punch, the elapsed clock) - when the server
 *  sends one, show the server's. */
export function nzClock(value) {
  const d = parseServerInstant(value);
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: NZ_TZ, hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d);
  } catch {
    // Ancient WebView with no tz database: the phone is in NZ anyway.
    return d.toTimeString().slice(0, 5);
  }
}

/* Device wall-clock time as ISO-8601 WITH its offset (2026-09-12T06:58:12+12:00).
 * The offset is the point: it tells the server what time the driver believes
 * it was, independent of when the punch finally arrives, and lets the server
 * record the skew if the phone's clock is wrong. */
function deviceIso(date = new Date()) {
  const pad = (n) => String(Math.abs(Math.trunc(n))).padStart(2, '0');
  const offMin = -date.getTimezoneOffset();
  const sign = offMin >= 0 ? '+' : '-';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    + `${sign}${pad(offMin / 60)}:${pad(offMin % 60)}`;
}

/** The device's calendar date, "YYYY-MM-DD". Used ONLY to decide whether a
 *  pre-check the phone remembers is still today's; the check's real date is
 *  the server's, worked out from device_time and its offset. Never sent. */
export function deviceDate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Stable id for the punch, generated on the device so a replayed send cannot
 *  create a second shift. crypto.randomUUID is missing on older Android
 *  WebViews and on plain-http origins. */
export function uuid() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return `d-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }
}

/* ------------------------------------------------------------------ *
 * last known shift state (localStorage)
 * ------------------------------------------------------------------ */

/* The service worker never caches API responses, so on a cold open with no
 * signal there is nothing to ask. Without this the bar would offer "Start
 * shift" to a driver who is already four hours in. */
export function cachedShift() {
  try {
    const raw = localStorage.getItem(SHIFT_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(state) {
  try {
    localStorage.setItem(SHIFT_CACHE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch {
    /* private mode / quota - the bar just falls back to the API */
  }
  return state;
}

/* The day's pre-check travels in the shift cache under `check`, because the
 * two have one lifetime: the amber "fault reported" card has to survive an
 * app restart with no signal and disappear when the shift ends. /time/current
 * knows nothing about checks, so a server answer must not wipe it: it is kept
 * while a shift is open, or while it is still today's (a driver can do the
 * check before pressing Start shift). */
function keepCheck(check, openShift) {
  if (!check) return null;
  if (openShift) return check;
  return check.check_date === deviceDate() ? check : null;
}

function stateFrom(payload) {
  const open = payload?.open_shift ?? null;
  return {
    open_shift: open,
    today: payload?.today ?? null,
    fortnight: payload?.fortnight ?? null,
    check: keepCheck(cachedShift()?.check, open),
    stale: false,
  };
}

/* The contract writes clock-in/out/break as "-> {shift}". Accept both a
 * wrapper and a bare shift so a Track B change of mind cannot blank the bar. */
function shiftFrom(data) {
  if (!data) return null;
  if (data.shift !== undefined) return data.shift;
  return (data.id || data.started_at) ? data : null;
}

/* POST /checks answers "-> {check}"; accept a bare check for the same reason. */
function checkFrom(data) {
  if (!data) return null;
  if (data.check !== undefined) return data.check;
  return data.items ? data : null;
}

/* A shift the server has confirmed. A finished one ends the fault card with
 * it: the office holds the record from here, the cab does not need nagging
 * about a truck it has handed back. */
function applyServerShift(shift) {
  const base = cachedShift() || {};
  const open = shift && !shift.finished_at ? shift : null;
  const check = shift && shift.finished_at ? null : (base.check ?? null);
  return writeCache({ ...base, open_shift: open, check, stale: false });
}

/* The check as the bar shows it while it is still only on the phone - the
 * same shape the server serialises, so the summary screen has one code path. */
function localCheck(event) {
  return {
    id: null,
    client_uuid: event.client_uuid,
    truck_id: event.truck_id,
    truck_plate: event.truck_plate || null,
    check_date: event.check_date,
    hubo_reading: event.hubo_reading ?? null,
    items: event.items || {},
    faults: event.faults || [],
    faults_note: event.faults_note || '',
    item_notes: event.item_notes || {},
    status: event.status || 'ok',
    action_taken: null,
    submitted_at: event.occurred_at,
    submitted_nz: nzClock(event.occurred_at),
    pending: true,
  };
}

/** Put an already-recorded check (today's, from /checks/today) back in the
 *  cache - a driver who checked the truck before pressing Start shift, or
 *  who clocks back in after lunch, keeps the fault card without a second
 *  form. Returns the new state, or null when storage is off. */
export function rememberCheck(check) {
  if (!check) return null;
  const base = cachedShift() || { open_shift: null, today: null, fortnight: null };
  return writeCache({ ...base, check: { ...check, pending: !!check.pending } });
}

/* Is `check` the one the cache holds? By server id when both have one, else
 * by the uuid the phone minted - a check still in the queue has no id yet. */
function sameCheck(a, b) {
  if (!a || !b) return false;
  if (a.id && b.id) return String(a.id) === String(b.id);
  return !!a.client_uuid && a.client_uuid === b.client_uuid;
}

/** The server's newer copy of a check the phone holds (a comment the driver
 *  added later in the day): swap it in so the fault card and the sheet say
 *  the same thing. A check the cache does not hold is left alone - the bar
 *  must not start showing a sheet the driver merely looked at. Returns the
 *  new state, or null when nothing was replaced. */
export function replaceCachedCheck(check) {
  const base = cachedShift();
  if (!check || !sameCheck(base?.check, check)) return null;
  return writeCache({
    ...base,
    check: { ...base.check, ...check, truck_plate: check.truck_plate || base.check.truck_plate || null, pending: false },
  });
}

/** The office says today's check is NOT done - it voided the one this phone
 *  still holds. Left in the cache it would keep the fault card up and tell
 *  the driver the sheet is signed when the office needs it done again.
 *  Returns the new state, or null when the cache held something else. */
export function forgetCachedCheck(check) {
  const base = cachedShift();
  if (!check || !sameCheck(base?.check, check)) return null;
  return writeCache({ ...base, check: null });
}

/* Whole minutes between two instants the phone holds - a server naive-UTC
 * string or a device ISO with its offset, parseServerInstant reads both.
 * Only for a break the phone closed itself; the server's `minutes` replaces
 * it on the next sync. */
function minutesBetween(startedAt, finishedAt) {
  const a = parseServerInstant(startedAt);
  const b = parseServerInstant(finishedAt);
  if (!a || !b) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 60000));
}

/* A break as the bar shows it while the start punch is still only on the
 * phone - the shape serialize_shift uses, so the bar has one code path. */
function localBreak(event) {
  return {
    id: `pending:${event.client_uuid}`,
    client_uuid: event.client_uuid,
    start: nzClock(event.occurred_at),
    finish: null,
    started_at: event.occurred_at,
    finished_at: null,
    minutes: 0,
    source: 'clock',
    entered_by: null,
    open: true,
    pending: true,
  };
}

function isOpenBreak(b) { return !!b && (b.open || !b.finished_at); }

/* The shift with its break rows replaced and on_break / break_minutes
 * re-derived from them the way the server does it (sum of the FINISHED
 * breaks), so the total and the times cannot disagree. A shift left with no
 * rows keeps the total it already had - a Log Ninja day, a legacy minutes
 * punch - which is the server's rule too. */
function withBreaks(open, breaks) {
  return {
    ...open,
    breaks,
    on_break: breaks.some(isOpenBreak),
    break_minutes: breaks.length
      ? breaks.reduce((t, b) => t + (isOpenBreak(b) ? 0 : (Number(b.minutes) || 0)), 0)
      : (open.break_minutes || 0),
    pending: true,
  };
}

/* What one punch does to a shift state, as a pure function: the same code
 * path serves a punch just queued (laid on the cached state, applyLocally)
 * and a punch still queued when the office answers (laid on the server's
 * state, overlayQueued). Returns `state` itself - the same object - when the
 * punch changes nothing: a break punch with no shift open, a break start the
 * shift already lists, an end with no break to close. Never writes the
 * cache. */
function applyEvent(state, event) {
  const open = state.open_shift;
  if (event.kind === 'in') {
    return {
      ...state,
      open_shift: {
        id: `pending:${event.client_uuid}`,
        start: nzClock(event.occurred_at),
        started_at: event.occurred_at,
        finish: null,
        finished_at: null,
        break_minutes: 0,
        breaks: [],
        on_break: false,
        day_type: 'normal',
        source: 'clock',
        truck_id: event.truck_id || null,
        truck_plate: event.truck_plate || null,
        pending: true,
      },
    };
  }
  if (event.kind === 'out') return { ...state, open_shift: null, check: null };
  /* A break belongs to a shift. With none on the phone there is nothing to
   * show it against; the punch still queues and the office decides. */
  if (!open) return state;
  if (event.kind === 'break') {
    return {
      ...state,
      open_shift: { ...open, break_minutes: (open.break_minutes || 0) + (event.minutes || 0), pending: true },
    };
  }
  if (event.kind === 'break_start') {
    const breaks = open.breaks || [];
    if (breaks.some((b) => b.client_uuid === event.client_uuid)) return state;
    // break_minutes is untouched: the server derives it from FINISHED rows
    // only when a row finishes, and an open break has no minutes yet
    return {
      ...state,
      open_shift: { ...open, breaks: [...breaks, localBreak(event)], on_break: true, pending: true },
    };
  }
  if (event.kind === 'break_end') {
    /* Close the LAST open break - the one this end punch follows in the
     * queue. The end punch's id is kept on the row so a refused end can find
     * the break it closed and open it again (unwindBreak). */
    const breaks = [...(open.breaks || [])];
    let i = breaks.length - 1;
    while (i >= 0 && !isOpenBreak(breaks[i])) i -= 1;
    if (i < 0) return state;
    breaks[i] = {
      ...breaks[i],
      finish: nzClock(event.occurred_at),
      finished_at: event.occurred_at,
      minutes: minutesBetween(breaks[i].started_at, event.occurred_at),
      open: false,
      end_client_uuid: event.client_uuid,
      pending: true,
    };
    return { ...state, open_shift: withBreaks(open, breaks) };
  }
  return state;
}

/* What the bar must show until the next successful sync. A queued punch that
 * left the UI unchanged would be punched again, and again. Returns null when
 * the punch changed nothing on the phone (a break with no cached shift), so
 * the caller keeps the state it is already showing rather than a blank one. */
function applyLocally(kind, event) {
  const base = cachedShift() || { open_shift: null, today: null, fortnight: null };
  const next = kind === 'check'
    ? { ...base, check: localCheck(event) }
    : applyEvent(base, { ...event, kind });
  if (next === base) return null;
  return writeCache({ ...next, stale: true });   // shown from the device, not confirmed
}

/* ------------------------------------------------------------------ *
 * queue
 * ------------------------------------------------------------------ */

async function add(event) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').add({ ...event, queuedAt: Date.now(), attempts: 0, status: 'pending' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/* A check the office refused while the phone was online. It is banked as
 * already-failed so it lands in the same red card as a refused punch: the
 * shift has still started, and the office will otherwise see a shift with no
 * check and nobody will know why. */
async function addFailed(event, reason) {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const req = tx(db, 'readwrite').add({
        ...event, queuedAt: Date.now(), attempts: 1, status: 'failed', failedAt: Date.now(), reason,
      });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    /* IndexedDB off - the toast is all the driver gets */
  }
}

export async function allEvents() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readonly').getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function remove(id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const req = tx(db, 'readwrite').delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function update(id, patch) {
  const db = await openDb();
  const store = tx(db, 'readwrite');
  return new Promise((resolve) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row) return resolve();
      store.put({ ...row, ...patch });
      return resolve();
    };
    get.onerror = () => resolve();
  });
}

/** Punches still waiting for signal. Failed ones are counted separately - they
 *  are not going to send on their own and need a human. */
export async function pendingCount() {
  try {
    return (await allEvents()).filter((e) => e.status !== 'failed').length;
  } catch {
    return 0;
  }
}

/** Punches the server refused. Surfaced in the UI until the driver dismisses
 *  them, so nobody finds out at payday. */
export async function failedEvents() {
  try {
    return (await allEvents()).filter((e) => e.status === 'failed');
  } catch {
    return [];
  }
}

export async function dismissFailed(id) {
  try {
    await remove(id);
  } catch {
    /* nothing the driver can do about it */
  }
}

/* Oldest first. The IndexedDB key is auto-incremented by add(), so it alone
 * gives the order the punches were made in: a check and the clock-in queued
 * straight after it can share a millisecond, but two rows can never share an
 * id. queuedAt is only a tiebreak for a row without an id, which add() never
 * produces. */
function sortQueue(items) {
  return [...items].sort((a, b) => ((a.id || 0) - (b.id || 0)) || ((a.queuedAt || 0) - (b.queuedAt || 0)));
}

/* The office's answer with the punches still held on this phone laid over
 * it, in queue order. /time/current and a punch's own reply describe what
 * the office has SEEN; a punch queued behind that (a break end waiting on
 * the start just sent, a clock-out held in a dead zone) is not in it yet,
 * and a bar drawn from the bare answer would ask the driver to make that
 * punch again. Failed punches are left out: they live in the red card, not
 * on the bar. Returns `state` itself when nothing is queued. */
async function overlayQueued(state) {
  let items;
  try {
    items = await allEvents();
  } catch {
    return state;
  }
  const next = sortQueue(items.filter((e) => e.status !== 'failed')).reduce(applyEvent, state);
  return next === state ? state : { ...next, stale: true };
}

/* ------------------------------------------------------------------ *
 * sending
 * ------------------------------------------------------------------ */

const PATHS = {
  in: '/time/clock-in',
  out: '/time/clock-out',
  break: '/time/break',              // legacy minutes total - queued by an older app
  break_start: '/time/break/start',
  break_end: '/time/break/end',
  check: '/checks',
};

/* Only the fields the contract names leave the phone. The queued row also
 * carries display-only things (truck_plate, the fault labels) that the
 * server must not be asked to store. A break start/end sends the same
 * {client_uuid, occurred_at} a clock-in does (+ note/lat/lon when present). */
function body(event) {
  if (event.kind === 'check') {
    const payload = {
      client_uuid: event.client_uuid,
      truck_id: event.truck_id,
      items: event.items || {},
      // the device clock with its offset, same reasoning as occurred_at: the
      // check was done in the yard at 06:40 whenever the phone finds signal
      device_time: event.occurred_at,
    };
    if (event.hubo_reading != null) payload.hubo_reading = event.hubo_reading;
    if (event.faults_note) payload.faults_note = event.faults_note;
    if (event.item_notes && Object.keys(event.item_notes).length) payload.item_notes = event.item_notes;
    if (event.lat != null) { payload.lat = event.lat; payload.lon = event.lon; }
    return payload;
  }
  const payload = { client_uuid: event.client_uuid, occurred_at: event.occurred_at };
  if (event.kind === 'break') payload.minutes = event.minutes;
  if (event.kind === 'in') {
    // which truck went out, and the pre-check it belongs to; the server links
    // the two whichever order they arrive in
    if (event.truck_id) payload.truck_id = event.truck_id;
    if (event.check_client_uuid) payload.check_client_uuid = event.check_client_uuid;
  }
  if (event.note) payload.note = event.note;
  if (event.lat != null) { payload.lat = event.lat; payload.lon = event.lon; }
  return payload;
}

/* A punch is only abandoned when the server has made a decision about it.
 * No response (dead zone, DNS, aeroplane mode), 5xx, 408 and 507 are all
 * "try again later"; 401 is too, because api.js clears the token and the
 * driver logs back in - the punch must outlive that. */
function retryable(err) {
  const status = err?.response?.status;
  if (!status) return true;
  if (status === 401 || status === 408 || status === 507) return true;
  return status >= 500;
}

const BREAK_KINDS = ['break', 'break_start', 'break_end'];

function reasonFor(err, kind) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  const status = err?.response?.status;
  if (status === 409) {
    // the server always says which (not clocked in / already on a break /
    // no break open); this is only the fallback for an answer with no detail
    return BREAK_KINDS.includes(kind)
      ? 'The office could not match this break to an open shift'
      : 'The office already has a shift recorded for this time';
  }
  if (status === 423) return 'That fortnight is approved and locked - the office must change it';
  if (status === 404) return 'That truck is no longer on the fleet list';
  return status ? `Rejected by the server (${status})` : 'Could not reach the office';
}

async function post(API, event) {
  const res = await API.post(PATHS[event.kind], body(event));
  return res?.data;
}

/** Punch now if there is signal, otherwise hold it on the phone.
 *  -> { status: 'sent' | 'queued' | 'rejected', state, client_uuid, message? }
 *  A check answers with `check`, the punches with `shift`. */
async function dispatch(API, kind, extra = {}) {
  const event = {
    kind,
    client_uuid: uuid(),
    occurred_at: deviceIso(),
    ...extra,
  };
  const queued = async () => {
    await add(event);
    return { status: 'queued', state: applyLocally(kind, event), client_uuid: event.client_uuid };
  };

  if (!navigator.onLine) return queued();

  /* Anything still held on the phone goes first. A clock-in sent straight
   * away while its pre-check sits in the queue, or a clock-out overtaking a
   * held clock-in, would reach the office out of order - so drain the queue,
   * and if it is still stuck, join the back of it. */
  if ((await pendingCount()) > 0) {
    const { pending } = await flushClock(API);
    if (pending > 0) return queued();
  }

  let data;
  try {
    data = await post(API, event);
  } catch (err) {
    if (retryable(err)) return queued();
    const message = reasonFor(err, kind);
    if (kind === 'check') {
      /* Refused for good (unknown truck, an item the server does not know).
       * The shift still starts - a fault never strands a run, and neither
       * does a form - but the refusal must stay visible until seen. */
      await addFailed(event, message);
      return { status: 'rejected', state: cachedShift(), message, client_uuid: event.client_uuid };
    }
    /* The server refused outright (already clocked in, fortnight locked).
     * Queuing it would retry a decision that will never change, so say so
     * now instead of banking a punch that silently dies on the next flush. */
    return { status: 'rejected', state: cachedShift(), message, client_uuid: event.client_uuid };
  }

  if (kind === 'check') {
    const check = checkFrom(data) || localCheck(event);
    const state = writeCache({
      ...(cachedShift() || {}),
      check: { ...check, truck_plate: check.truck_plate || event.truck_plate || null, pending: false },
    });
    return { status: 'sent', state, check, client_uuid: event.client_uuid };
  }
  const shift = shiftFrom(data);
  let state = applyServerShift(shift);
  /* The queue was drained before this punch went, so it is normally empty
   * here - but the reply only knows what the office has seen, and anything
   * still held on the phone must not vanish from the bar. */
  if ((await pendingCount()) > 0) state = writeCache(await overlayQueued(state));
  return { status: 'sent', state, shift, client_uuid: event.client_uuid };
}

/** opts may carry truck_id, truck_plate (display only) and check_client_uuid
 *  - the pre-check this shift starts on. */
export function clockIn(API, opts = {}) { return dispatch(API, 'in', opts); }
export function clockOut(API, opts = {}) { return dispatch(API, 'out', opts); }

/** A break is recorded as two punches, start and end, each with the device
 *  time - "30 min" with no time behind it cannot be checked against anything.
 *  Both answer "-> {shift, break}"; the shift carries `breaks` / `on_break`.
 *  Paid either way: the server never deducts a break from the hours. */
export function startBreak(API, opts = {}) { return dispatch(API, 'break_start', opts); }
export function endBreak(API, opts = {}) { return dispatch(API, 'break_end', opts); }

/** Legacy: a bare minutes total. Kept so nothing that still imports it
 *  breaks and so an older app's queued punch has a sender; the driver app no
 *  longer offers it. */
export function addBreak(API, minutes, opts = {}) {
  return dispatch(API, 'break', { minutes, ...opts });
}

/* A note may be up to this long; the server truncates nothing, it refuses. */
export const ITEM_NOTE_MAX = 500;

/* Per-item notes, only where the driver actually wrote something. A key that
 * is not on the sheet, or a note over the limit, is refused by the server
 * along with the WHOLE check - so the payload is cleaned here rather than
 * risking a form that fails for a stray key from an older cache. */
function cleanNotes(raw, items) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw).forEach((k) => {
    if (!(k in items)) return;
    const text = String(raw[k] ?? '').trim().slice(0, ITEM_NOTE_MAX);
    if (text) out[k] = text;
  });
  return out;
}

/** The daily pre-check, from PreCheck.js. `payload` is the POST /checks body
 *  minus client_uuid and device_time (minted here); `meta` is what the bar
 *  needs to name the truck and list the faults before the server has seen
 *  it. Call this BEFORE clockIn so the queue order is check, then punch. */
export function submitCheck(API, payload, meta = {}) {
  const items = payload?.items || {};
  const itemNotes = cleanNotes(payload?.item_notes, items);
  const faultKeys = Object.keys(items).filter((k) => items[k] === 'fault');
  // each fault carries its note so the bar can say "Mirrors - cracked left"
  // before the server has echoed the check back
  const faults = ((meta.faults || []).length
    ? meta.faults
    : faultKeys.map((k) => ({ key: k, label: k }))
  ).map((f) => ({ ...f, note: f.note || itemNotes[f.key] || null }));
  return dispatch(API, 'check', {
    truck_id: payload?.truck_id,
    hubo_reading: payload?.hubo_reading ?? null,
    items,
    faults_note: payload?.faults_note || '',
    item_notes: itemNotes,
    lat: payload?.lat,
    lon: payload?.lon,
    check_date: deviceDate(),
    status: faultKeys.length ? 'fault' : 'ok',
    faults,
    truck_plate: meta.truck_plate || null,
  });
}

/** Current shift + fortnight totals, cached for the next cold offline open.
 *  Returns the cached state (marked stale) when the office cannot be reached. */
export async function getCurrent(API, personId) {
  try {
    const res = await API.get('/time/current', personId ? { params: { person_id: personId } } : undefined);
    const state = stateFrom(res?.data);
    /* The office's truth, plus whatever this phone still holds - a queued
     * punch is not in the answer yet and must not be wiped off the bar by
     * it. Someone else's record (an office view) has nothing of this
     * phone's on it, so it is cached as answered. */
    return writeCache(personId ? state : await overlayQueued(state));
  } catch {
    const cached = cachedShift();
    return cached ? { ...cached, stale: true } : { open_shift: null, today: null, fortnight: null, stale: true };
  }
}

/** Send everything held on the phone, oldest first.
 *  -> { sent, pending, failed }
 *
 *  Order is enforced: the first punch that still cannot send stops the run.
 *  Replaying a clock-out ahead of the clock-in it belongs to would be refused
 *  ("no open shift") and would turn a recoverable queue into a lost day. */
export async function flushClock(API) {
  let items = [];
  try {
    items = sortQueue(await allEvents());
  } catch {
    return { sent: 0, pending: 0, failed: 0 };
  }

  let sent = 0;
  let failed = 0;
  let stopped = false;

  for (const item of items) {
    if (item.status === 'failed') { failed += 1; continue; }
    if (stopped) continue;
    try {
      const data = await post(API, item);
      let synced = false;
      if (item.kind === 'check') {
        const base = cachedShift();
        if (base?.check?.client_uuid === item.client_uuid) {
          const check = checkFrom(data) || base.check;
          writeCache({
            ...base,
            check: { ...check, truck_plate: check.truck_plate || base.check.truck_plate || null, pending: false },
          });
        }
      } else {
        const shift = shiftFrom(data);
        if (shift) {
          applyServerShift(shift);
          synced = true;
        }
      }
      await remove(item.id);
      sent += 1;
      /* The answer just cached is the office's view, which stops at this
       * punch. Whatever is still queued behind it goes back on top - so a
       * break end waiting on the start that just went keeps the bar on
       * "Start break" with "1 to send", instead of asking for the end
       * again. Done after remove() so the punch just sent is not applied
       * twice. */
      if (synced && (await pendingCount()) > 0) {
        const base = cachedShift();
        if (base) writeCache(await overlayQueued(base));
      }
    } catch (err) {
      if (retryable(err)) {
        await update(item.id, { attempts: (item.attempts || 0) + 1, lastTriedAt: Date.now() });
        stopped = true;
      } else {
        const reason = reasonFor(err, item.kind);
        await update(item.id, { status: 'failed', failedAt: Date.now(), reason });
        failed += 1;
        /* A refused check must stop claiming "held on this phone" - the
         * summary screen says the office turned it down instead. The punch
         * behind it still sends: the shift is real even if the form was not. */
        if (item.kind === 'check') {
          const base = cachedShift();
          if (base?.check?.client_uuid === item.client_uuid) {
            writeCache({ ...base, check: { ...base.check, pending: false, failed: reason } });
          }
        } else if (item.kind === 'break_start' || item.kind === 'break_end') {
          unwindBreak(item);
        }
      }
    }
  }

  const pending = (await pendingCount());
  return { sent, pending, failed };
}

/* A break punch the office refused for good is out of the queue, so what it
 * did to the bar is undone. A refused START takes its pending break off the
 * shift - the office never recorded one - and a refused END re-opens the
 * break the phone had closed with it, so the bar offers End break again
 * rather than showing a break that quietly never ended. A break the SERVER
 * closed carries no end punch id and is left as it is. Either way the red
 * card carries the refusal. */
function unwindBreak(event) {
  const base = cachedShift();
  const open = base?.open_shift;
  const before = open?.breaks || [];
  if (!before.length) return;
  const breaks = event.kind === 'break_start'
    ? before.filter((b) => b.client_uuid !== event.client_uuid)
    : before.map((b) => (b.end_client_uuid === event.client_uuid
      ? { ...b, finish: null, finished_at: null, minutes: 0, open: true, end_client_uuid: null, pending: true }
      : b));
  const changed = breaks.length !== before.length || breaks.some((b, i) => b !== before[i]);
  if (changed) writeCache({ ...base, open_shift: withBreaks(open, breaks) });
}
